import React from 'react';
import { useDispatch } from 'react-redux';

import { withStyles, WithTheme } from '@material-ui/core';
import Select, { SelectProps } from 'core/Select';
import { debounce, get, map, omit, sortBy } from 'lodash';

import patientFormsResource from '@lib/resources/patientFormsResource';

export type TextFieldMedicationSearchValues = { value: string; label: string }[];

export interface TextFieldMedicationSearchProps
  extends Omit<SelectProps, 'classes' | 'loadOptions' | 'onChange'>,
    WithTheme {
  onChange: (values: TextFieldMedicationSearchValues) => void;
  shouldAutoFocus: boolean;
}

const TextFieldMedicationSearch: React.FC<TextFieldMedicationSearchProps> = ({ theme, shouldAutoFocus, ...rest }) => {
  const dispatch = useDispatch();
  // when using debounce on loadOptions, it's mandatory to use callback instead of promise
  const loadOptions = (search: string, callback: (values: TextFieldMedicationSearchValues) => void) => {
    if (!search || search.length <= 3) return callback([]);

    dispatch(
      patientFormsResource.medicationSearch({
        // E.g.: ATORVASTATIN CALCIUM
        params: { search, limit: 30 },
        meta: {
          callbackSuccess: (response) => {
            const value = map(get(response, 'data.response'), (medication) => {
              // d stands for drugName, s for strength
              const formattedName = `${medication.d} - ${medication.s}`;
              return {
                value: formattedName,
                label: formattedName,
              };
            });

            callback(sortBy(value, 'label'));
          },
          callbackError: () => callback([]),
        },
      }),
    );
  };

  return (
    <Select
      margin="none"
      menuPlacement="top"
      isMulti
      isClearable={false}
      hideNoResults
      hideSelectedOptions
      noAdornment
      shouldAutoFocus={shouldAutoFocus}
      breakWhiteSpace
      TextFieldProps={{
        InputProps: {
          disableUnderline: true,
          // WORKAROUND: We can't pass masked inputs to `react-select` components
          ...omit(get(rest, 'InputProps'), ['inputComponent']),
        },
      }}
      loadOptions={debounce(loadOptions, 300)}
      {...rest}
    />
  );
};

export default withStyles({}, { name: 'TextFieldMedicationSearch', withTheme: true })(TextFieldMedicationSearch);
